// API client for the standalone chat backend (server/). JWT in localStorage.
import { PREVIEW, mockResponse } from './mock';

export const API_BASE: string =
  (import.meta as any).env?.VITE_CHAT_API || 'http://localhost:8081';
export const WS_URL: string =
  (import.meta as any).env?.VITE_CHAT_WS || 'ws://localhost:8000/connection/websocket';

const TOKEN_KEY = 'fxmchat_token';
// In UI-preview mode a synthetic token makes ChatAuth call api.me() (mock-answered)
// instead of showing login. No real token is ever written.
let token: string | null = PREVIEW ? 'preview-token' : localStorage.getItem(TOKEN_KEY);

export function getToken(): string | null {
  return token;
}
export function setToken(t: string | null): void {
  token = t;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

// Persistent refresh token — privacy browsers (Ulaa) clear the httpOnly cookie
// on exit, which forced a full 2FA login every day. The cookie remains the
// primary path; this localStorage copy is replayed via X-Refresh-Token only
// when the cookie is missing. Trade-off (accepted): localStorage is readable
// by injected script where httpOnly is not.
const REFRESH_KEY = 'fxmchat_refresh';
function storedRefresh(): string {
  try { return localStorage.getItem(REFRESH_KEY) || ''; } catch { return ''; }
}
export function storeRefresh(rt?: string | null): void {
  try {
    if (rt) localStorage.setItem(REFRESH_KEY, rt);
  } catch { /* storage unavailable */ }
}
export function clearRefresh(): void {
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
}

// One in-flight refresh at a time — concurrent 401s all await the same attempt.
let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        // credentials: 'include' sends the httpOnly refresh cookie (cross-origin).
        const stored = storedRefresh();
        const r = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          // Cookie-first server-side; the header only matters when the browser
          // wiped the cookie (privacy browsers clearing cookies on exit).
          headers: stored ? { 'X-Refresh-Token': stored } : undefined,
        });
        if (!r.ok) return false;
        const data = await r.json().catch(() => null);
        if (data?.token) {
          setToken(data.token);
          storeRefresh(data.refresh_token);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

async function req(path: string, opts: RequestInit = {}, retried = false): Promise<any> {
  if (PREVIEW) return mockResponse(path, opts);
  const resp = await fetch(API_BASE + path, {
    ...opts,
    // Never serve a stale cached response — e.g. re-fetching members right after a
    // removal must reflect the change, not the browser's cached list.
    cache: 'no-store',
    // The API is a different origin (helm vs helm-api): without credentials the
    // browser DROPS the Set-Cookie from login/TOTP responses, the httpOnly
    // refresh cookie never exists, and every session dies at access-token expiry
    // with "Invalid or expired session". The cookie is scoped to /auth only.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (resp.status === 401 && !retried && token && !path.startsWith('/auth/')) {
    // Access token expired — exchange the refresh cookie for a new one and retry once.
    if (await tryRefresh()) return req(path, opts, true);
    // Refresh failed too: the session is truly gone. Go back to sign-in instead of
    // surfacing an "Invalid or expired session" alert on every action.
    setToken(null);
    window.location.assign('/');
  }
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || `${resp.status} ${resp.statusText}`);
  }
  if (resp.status === 204) return null;
  const json = await resp.json();
  // Every auth response that rotates the refresh cookie also returns the token —
  // keep the localStorage copy in sync so cookie-clearing browsers survive.
  if (path.startsWith('/auth/') && json && typeof json === 'object' && json.refresh_token) {
    storeRefresh(json.refresh_token);
  }
  return json;
}

export type Prefs = Record<string, any>;

// Status Context + Time Zone Intelligence — the mode/expiry/auto-reply + work-hours
// fields the backend now carries on the user record.
export type StatusMode = 'active' | 'focus' | 'async' | 'dnd';

// Fields shared by self (User) and peers (PublicUser/DmParty). auto_reply is self-only.
export type StatusTzFields = {
  status_mode?: StatusMode | null;
  status_expires_at?: string | null;
  timezone?: string | null; // IANA, e.g. 'America/New_York'
  work_start_min?: number | null; // local minutes past midnight (540 = 09:00)
  work_end_min?: number | null;
  work_days?: string | null; // csv of weekday nums, 0=Sun..6=Sat, e.g. '1,2,3,4,5'
};

export type User = StatusTzFields & {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string | null;
  status_emoji?: string | null;
  status_text?: string | null;
  status_auto_reply?: string | null; // self only
  presence?: string;
  notifications_paused?: boolean;
  theme?: string;
  prefs?: Prefs;
  ai_features_enabled?: boolean; // false = Helm Intelligence off; hide tone check / suggestions
};
export type PublicUser = StatusTzFields & {
  id: string;
  email?: string;
  name?: string;
  avatar_url?: string | null;
  status_emoji?: string | null;
  status_text?: string | null;
  presence?: string;
  role?: string;
};
export type FileItem = Attachment & {
  channel_id: string;
  message_id: string;
  sender_type: string;
  sender_id: string;
  created_at?: string;
};
export type ActivityItem = {
  type: string; // reaction | mention
  emoji?: string;
  actor: string;
  actor_name: string;
  channel_id?: string | null;
  channel_name?: string | null;
  message_id: string;
  body: string;
  created_at?: string;
};
export type Workspace = { id: string; name: string; slug: string; role?: string };
export type DiscoverableWorkspace = {
  id: string;
  name: string;
  slug: string;
  member_count: number;
  request_status: 'pending' | 'approved' | 'denied' | null;
};
export type MyJoinRequest = {
  id: string;
  workspace_id: string;
  workspace_name: string;
  status: 'pending' | 'approved' | 'denied';
};
export type JoinRequest = { id: string; user: PublicUser; message?: string | null; created_at?: string };
export type MyOrg = {
  linked: boolean;
  org: {
    customer_id: string;
    org_name: string;
    is_managed: boolean;
    role: 'owner' | 'admin' | 'member';
    fma_count: number;
    workspace_id: string | null;
    already_member: boolean;
  } | null;
};
export type DocMeta = {
  id: string;
  channel_id: string;
  workspace_id: string;
  title: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
};

// Vault — workspace-level artifact graph
export type VaultNode = {
  id: string;
  workspace_id: string;
  kind: 'doc' | 'file' | 'kb_entry' | 'diagram' | 'excerpt' | 'canvas' | 'bookmark' | 'task';
  title: string;
  summary?: string | null;
  tags: string[];
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
  completed_at?: string | null; // task nodes: null = open
  doc_id?: string | null;
  file_url?: string | null;
  file_mime?: string | null;
  file_size?: number | null;
  kb_entry_id?: string | null;
  source_message_id?: string | null;
  source_channel_id?: string | null;
  pinned_in?: string[];
  links?: VaultLink[];
};
export type VaultLink = {
  id: string;
  source_id: string;
  target_id: string;
  link_type: 'reference' | 'suggested' | 'embeds';
  created_by: string;
  confidence?: number | null;
  created_at: string | null;
};
export type VaultNeighborhood = {
  nodes: VaultNode[];
  links: VaultLink[];
};

// Helm intelligence — approval cards
export type HelmSuggestion = {
  id: string;
  workspace_id: string;
  channel_id: string;
  target_user_id: string;
  kind: 'response' | 'action_item' | 'doc_link' | 'knowledge_gap';
  payload: Record<string, any>;
  confidence: number;
  status: 'pending' | 'accepted' | 'dismissed' | 'edited';
  trigger_message_id: string | null;
  created_at: string | null;
};

// Incident war rooms — a structured coordination layer on a channel.
export type IncidentSeverity = 'sev1' | 'sev2' | 'sev3' | 'sev4';
export type IncidentStatus = 'open' | 'mitigating' | 'monitoring' | 'resolved';
export type IncidentMarker = { id: string; severity: IncidentSeverity; status: IncidentStatus; title: string };
export type IncidentTimelineEntry = {
  id: string;
  kind: string; // declared|status|severity|commander|role|note|action|resolved|reopened
  body: string;
  actor_id: string | null;
  actor_name: string | null;
  created_at: string | null;
};
export type Incident = {
  id: string;
  workspace_id: string;
  channel_id: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  commander_id: string | null;
  roles: Record<string, string>;
  summary: string | null;
  started_at: string | null;
  resolved_at: string | null;
  people?: Record<string, { name: string; avatar_url?: string | null }>;
  timeline?: IncidentTimelineEntry[];
  channel_name?: string | null; // list endpoint only
  commander_name?: string | null; // list endpoint only
};

// Webhooks — incoming (external → channel) + outgoing (message → external).
export type IncomingWebhook = {
  id: string;
  label: string;
  channel_id: string;
  channel_name?: string | null;
  active: boolean;
  last_used_at?: string | null;
  created_at?: string | null;
  url?: string; // returned once, on create — not retrievable later
};
export type OutgoingWebhook = {
  id: string;
  label?: string | null;
  url: string;
  channel_id?: string | null;
  trigger_word?: string | null;
  active: boolean;
  secret: string; // full on create; masked in the list
  last_status?: number | null;
  last_delivered_at?: string | null;
  created_at?: string | null;
};

// Calendar — the app's own events (+ read-only synced Google/Microsoft events).
export type CalAttendee = { type: 'user'; id: string } | { type: 'email'; email: string };
export type JiraStatus = { connected: boolean; base_url?: string; email?: string; display_name?: string | null };
export type JiraIssue = {
  key: string;
  summary: string;
  status: string | null;
  status_category: string | null;   // new | indeterminate | done
  type: string | null;
  priority: string | null;
  assignee: string | null;
  updated: string | null;
  url: string | null;
};
export type JiraProject = { key: string; name: string; id: string };

export type StickyNote = {
  id: string;
  body: string;
  color: string;   // yellow | pink | blue | green | purple
  sort: number;
  created_at: string | null;
  updated_at: string | null;
};

export type FieldKind = 'text' | 'select' | 'tags';
// A field def + the caller's own value. value: string for text/select, string[] for tags.
export type CustomField = { id: string; label: string; kind: FieldKind; options: string[]; position: number; value: string | string[] | null };
// A peer's non-empty field value for the directory. value: string (text/select) or string[] (tags).
export type MemberField = { label: string; kind: FieldKind; value: string | string[] };

// A workspace member's profile. When private, only id/name/avatar_url/role come back.
export type MemberProfile = {
  id: string;
  name?: string | null;
  avatar_url?: string | null;
  role?: string;
  private: boolean;
  email?: string;
  status_emoji?: string | null;
  status_text?: string | null;
  status_mode?: string;
  status_expires_at?: string | null;
  timezone?: string | null;
  work_start_min?: number | null;
  work_end_min?: number | null;
  work_days?: string | null;
  presence?: string;
  fields?: MemberField[];
};

export type EventGuest = { name?: string | null; email?: string | null; response?: string | null; organizer?: boolean };

export type CalendarEvent = {
  id: string;
  source: 'local' | 'google' | 'microsoft';
  editable: boolean;
  title: string;
  description?: string | null;
  location?: string | null;
  start: string | null;
  end: string | null;
  all_day: boolean;
  is_call: boolean;
  channel_id?: string | null;
  attendees: CalAttendee[];
  external_provider?: string | null;
  external_link?: string | null;
  meeting_link?: string | null;
  recurrence?: string | null;
  reminder_minutes?: number | null;
  recurring?: boolean;
  guests?: EventGuest[];          // display-ready guest list (external events carry this)
  organizer?: string | null;      // external events: organizer name/email
};
export type CalendarEventInput = {
  title: string; start: string; end: string; all_day?: boolean; is_call?: boolean;
  description?: string; location?: string; attendees?: CalAttendee[];
  workspace_id?: string; channel_id?: string; sync_external?: boolean; provider?: string;
  recurrence?: string; reminder_minutes?: number;
};

export type Channel = {
  id: string;
  name?: string;
  kind: string;
  mode?: string;   // chat | forum
  topic?: string | null;
  unread?: boolean;
  mentions?: number;
  last_message_at?: string | null;
  last_read_at?: string | null;
  last_message?: { body: string; sender_name: string; mine: boolean } | null;
  created_by?: string | null;
  incident?: IncidentMarker | null; // set when the channel is an incident war room
};
export type Agent = { id: string; name: string; avatar_url?: string | null; scopes?: string[]; connected?: boolean; last_seen_at?: string | null };
export type ScopeItem = { scope: string; desc: string };
export type ScopeGroup = { group: string; scopes: ScopeItem[] };
export type ChannelMembers = {
  users: PublicUser[];
  agents: { id: string; name: string; avatar_url?: string | null }[];
  count: number;
};
export type TranscriptStatus = 'pending' | 'done' | 'error' | 'off' | 'quota';
export type Attachment = {
  id?: string;
  name?: string;
  url?: string;
  mime?: string;
  size?: number;
  alt?: string; // accessibility description (image uploads)
  type?: string; // e.g. 'poll' | 'prompt' | 'prompt_answer' — structured, non-file attachment
  poll_id?: string;
  prompt_id?: string;
  // Voice notes + video (Loom) clips
  kind?: 'audio' | 'video';
  duration_ms?: number;
  width?: number;
  height?: number;
  poster?: string; // video thumbnail url
  transcript?: string | null;
  transcript_status?: TranscriptStatus;
};
export type ReactionAgg = { emoji: string; count: number; actors: string[] };
export type DmParty = StatusTzFields & {
  type: string;
  id: string;
  name: string;
  avatar_url?: string | null;
  presence?: string;
  status_emoji?: string | null;
  status_text?: string | null;
};
export type Dm = Channel & { dm_with: DmParty };
export type Message = {
  /** client-only: optimistic local echo not yet confirmed by the server */
  pending?: boolean;
  id: string;
  channel_id: string;
  sender_type: string;
  sender_id: string;
  sender_name?: string;
  sender_avatar?: string | null;
  body: string;
  attachments?: Attachment[];
  reactions?: ReactionAgg[];
  created_at?: string;
  edited_at?: string | null;
  pinned?: boolean;
  pinned_at?: string | null;
  pinned_by?: string | null;
  is_announcement?: boolean;
  flagged_dlp?: boolean;
  // Threads: set on a reply; on a root, reply_count/last_reply_at summarize its thread.
  thread_root_id?: string | null;
  reply_count?: number;
  last_reply_at?: string | null;
  title?: string | null;   // forum mode: a root post's topic title
};
export type SentMessage = Message & { channel_name?: string | null; channel_kind?: string };

export type PollData = {
  id: string;
  channel_id: string;
  message_id: string | null;
  question: string;
  options: { id: string; text: string }[];
  allow_multi: boolean;
  anonymous: boolean;
  closes_at: string | null;
  closed: boolean;
  counts: Record<string, number>;
  my_votes: string[];
  total_voters: number;
  voters_by_option?: Record<string, string[]>;
};
export type PromptData = {
  id: string;
  channel_id: string;
  message_id: string | null;
  agent_id: string | null;
  question: string;
  options: { id: string; label: string }[];
  allow_multi: boolean;
  status: 'pending' | 'answered';
  selected: string[];
  answered_by: string | null;
  answered_at: string | null;
};
export type KudosEntry = { id: string; from: PublicUser; to: PublicUser; reason: string | null; created_at: string | null };
export type ChecklistItem = { id: string; text: string; done: boolean; position: number; done_by: string | null; created_at: string | null };
export type Ritual = { id: string; channel_id: string; prompt: string; cadence: string; day: number; hour_utc: number; enabled: boolean };
export type AutoRule = { id: string; rule_type: 'role' | 'field'; field_id: string | null; match_value: string };
export type EmbedWidget = { id: string; name: string; greeting: string | null; accent: string; require_email: boolean; enabled: boolean; allowed_origins: string; public_token: string; snippet: string };

export type SearchMessage = {
  message_id: string;
  channel_id: string;
  channel_name?: string | null;
  channel_kind: string;
  sender_type: string;
  sender_id: string;
  sender_name: string;
  body: string;
  created_at?: string;
};
export type SearchPerson = PublicUser & { kind: 'user' | 'agent' };
export type KbHit = {
  id: string; title: string; body: string; tags: string[]; status: string;
  source_channel_id?: string | null; similarity?: number;
};
export type SemanticHit = {
  id: string; channel_id: string; body: string; similarity: number; sender_name?: string | null;
};
export type SearchResults = {
  messages: SearchMessage[];
  files: (FileItem & { channel_name?: string | null })[];
  people: SearchPerson[];
  channels: Channel[];
  kb?: KbHit[];        // knowledge-base entries by meaning
  semantic?: SemanticHit[]; // messages related by meaning (not keyword)
};
export type SearchFilters = {
  after?: string; // ISO date
  before?: string; // ISO date
  sender?: string; // user id
  channel?: string; // channel id
  has_links?: boolean;
  has_reactions?: boolean;
  tagged_me?: boolean;
  file_type?: string; // extension, e.g. 'pdf'
};

// Status Context + Time Zone patch — any subset of these fields may be sent to PATCH /me.
export type MePatch = {
  status_emoji?: string | null;
  status_text?: string | null;
  status_mode?: StatusMode;
  status_expires_at?: string | null;
  status_auto_reply?: string | null;
  clear_status?: boolean;
  timezone?: string | null;
  work_start_min?: number | null;
  work_end_min?: number | null;
  work_days?: string | null;
};

// "Find a time" overlap suggestions (all times UTC; per-participant local_time precomputed).
export type OverlapParticipant = { user_id: string; local_time: string };
export type OverlapSuggestion = {
  start_utc: string;
  end_utc: string;
  participants: OverlapParticipant[];
};
export type OverlapResult = { suggestions: OverlapSuggestion[] };

export type AuthProviders = { google: boolean; microsoft: boolean };
export type AuthStage = 'totp_enroll' | 'totp';
export type LoginResult = { stage: AuthStage; token: string; user: User };
export type TotpEnroll = { secret: string; otpauth_uri: string; qr_svg: string };

export const api = {
  authProviders: (): Promise<AuthProviders> => req('/auth/providers'),
  oauthStartUrl: (provider: 'google' | 'microsoft'): string =>
    `${API_BASE}/auth/${provider}/start`,
  signup: (email: string, password: string): Promise<void> =>
    req('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password }) }),
  login: (email: string, password: string): Promise<LoginResult> =>
    req('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  resetRequest: (email: string): Promise<void> =>
    req('/auth/password-reset/request', { method: 'POST', body: JSON.stringify({ email }) }),
  resetConfirm: (token: string, newPassword: string): Promise<void> =>
    req('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
  exchange: (otc: string): Promise<LoginResult> =>
    req('/auth/exchange', { method: 'POST', body: JSON.stringify({ otc }) }),
  // Pre-MFA calls carry the in-memory pre-MFA token explicitly — it is never persisted.
  totpEnroll: (preMfa: string): Promise<TotpEnroll> =>
    req('/auth/totp/enroll', { method: 'POST', headers: { Authorization: `Bearer ${preMfa}` } }),
  totpActivate: (preMfa: string, code: string): Promise<{ token: string; recovery_codes: string[] }> =>
    req('/auth/totp/activate', {
      method: 'POST',
      headers: { Authorization: `Bearer ${preMfa}` },
      body: JSON.stringify({ code }),
    }),
  totpVerify: (preMfa: string, code: string): Promise<{ token: string }> =>
    req('/auth/totp/verify', {
      method: 'POST',
      headers: { Authorization: `Bearer ${preMfa}` },
      body: JSON.stringify({ code }),
    }),
  recovery: (preMfa: string, code: string): Promise<{ token: string; remaining: number }> =>
    req('/auth/recovery', {
      method: 'POST',
      headers: { Authorization: `Bearer ${preMfa}` },
      body: JSON.stringify({ code }),
    }),
  me: (): Promise<User> => req('/me'),
  realtimeToken: (): Promise<{ token: string }> => req('/realtime/token'),
  subscribeToken: (channel: string): Promise<{ token: string }> =>
    req(`/realtime/subscribe-token?channel=${encodeURIComponent(channel)}`),
  workspaces: (): Promise<Workspace[]> => req('/workspaces'),
  createWorkspace: (name: string): Promise<Workspace> =>
    req('/workspaces', { method: 'POST', body: JSON.stringify({ name }) }),

  // Workspace discovery + join requests (create-vs-join + owner/admin approval)
  discoverWorkspaces: (q: string): Promise<DiscoverableWorkspace[]> =>
    req(`/workspaces/discoverable?q=${encodeURIComponent(q)}`),
  requestJoin: (wid: string, message?: string): Promise<MyJoinRequest> =>
    req(`/workspaces/${wid}/join`, { method: 'POST', body: JSON.stringify({ message: message || null }) }),
  myJoinRequests: (): Promise<MyJoinRequest[]> => req('/me/join-requests'),

  // Linked management org (create-vs-join third option): discover + provision/enter
  getMyOrg: (): Promise<MyOrg> => req('/me/org'),
  joinMyOrg: (): Promise<Workspace> => req('/me/org/join', { method: 'POST' }),
  joinRequests: (wid: string): Promise<JoinRequest[]> => req(`/workspaces/${wid}/join-requests`),
  approveJoin: (wid: string, reqId: string): Promise<{ ok: boolean }> =>
    req(`/workspaces/${wid}/join-requests/${reqId}/approve`, { method: 'POST' }),
  denyJoin: (wid: string, reqId: string): Promise<{ ok: boolean }> =>
    req(`/workspaces/${wid}/join-requests/${reqId}/deny`, { method: 'POST' }),
  channels: (wid: string): Promise<Channel[]> => req(`/workspaces/${wid}/channels`),
  createChannel: (wid: string, name: string, kind = 'public', members: string[] = []): Promise<Channel> =>
    req(`/workspaces/${wid}/channels`, { method: 'POST', body: JSON.stringify({ name, kind, members }) }),
  deleteChannel: (cid: string): Promise<{ ok: boolean }> =>
    req(`/channels/${cid}`, { method: 'DELETE' }),
  callStatus: (cid: string): Promise<{ active: boolean; count: number }> =>
    req(`/channels/${cid}/call`),
  messages: (cid: string, opts?: { before?: string; limit?: number }): Promise<Message[]> => {
    const ps = new URLSearchParams();
    if (opts?.before) ps.set('before', opts.before);
    if (opts?.limit) ps.set('limit', String(opts.limit));
    const qs = ps.toString();
    return req(`/channels/${cid}/messages${qs ? `?${qs}` : ''}`);
  },
  sendMessage: async (cid: string, body: string, opts?: { title?: string }): Promise<Message> => {
    const m = await req(`/channels/${cid}/messages`, { method: 'POST', body: JSON.stringify({ body, ...(opts?.title ? { title: opts.title } : {}) }) });
    // Own sends never come back on the notify stream (the server excludes the
    // sender) — tell the app so deck previews update without a refresh.
    window.dispatchEvent(new CustomEvent('fx:message-sent', { detail: { channelId: cid, body } }));
    return m;
  },
  setChannelMode: (cid: string, mode: 'chat' | 'forum'): Promise<{ channel_id: string; mode: string }> =>
    req(`/channels/${cid}/mode`, { method: 'PATCH', body: JSON.stringify({ mode }) }),
  toneCheck: (text: string): Promise<{ available: boolean; verdict?: 'clear' | 'caution' | 'harsh'; issues?: string[]; rewrite?: string | null }> =>
    req('/me/tone-check', { method: 'POST', body: JSON.stringify({ text }) }),
  catchup: (cid: string): Promise<{ available: boolean; count: number; note?: string; summary: { tldr: string | null; decisions: string[]; action_items: { who: string | null; what: string }[]; open_questions: string[] } | null }> =>
    req(`/channels/${cid}/catchup`),
  routingSuggest: (text: string, channelId: string): Promise<{ available: boolean; channels: { id: string; name: string; score: number; matches: number }[]; people: { id: string; name: string; score: number; matches: number }[] }> =>
    req('/routing/suggest', { method: 'POST', body: JSON.stringify({ text, channel_id: channelId }) }),
  kbAnswer: (wid: string, q: string): Promise<{ entry?: KbHit }> =>
    req(`/workspaces/${wid}/kb/answer?q=${encodeURIComponent(q)}`),
  kbScan: (cid: string): Promise<{ available: boolean; found: number; drafts: number }> =>
    req(`/channels/${cid}/kb/scan`, { method: 'POST' }),
  kbList: (wid: string, opts?: { status?: 'draft' | 'published'; q?: string }): Promise<{ entries: { id: string; title: string; body: string; tags: string[]; status: string; source_channel_id: string | null; created_at: string; sim?: number }[] }> => {
    const p = new URLSearchParams();
    if (opts?.status) p.set('status', opts.status);
    if (opts?.q) p.set('q', opts.q);
    const qs = p.toString();
    return req(`/workspaces/${wid}/kb${qs ? `?${qs}` : ''}`);
  },
  kbPublish: (wid: string, id: string): Promise<{ ok: boolean; status: string }> =>
    req(`/workspaces/${wid}/kb/${id}/publish`, { method: 'POST' }),
  kbDelete: (wid: string, id: string): Promise<void> =>
    req(`/workspaces/${wid}/kb/${id}`, { method: 'DELETE' }),
  markRead: (cid: string, at?: string): Promise<{ ok: boolean; last_read_at: string }> =>
    req(`/channels/${cid}/read`, { method: 'POST', body: JSON.stringify(at ? { at } : {}) }),
  channelReads: (cid: string): Promise<{ user_id: string; last_read_at: string | null }[]> =>
    req(`/channels/${cid}/reads`),
  editMessage: (mid: string, body: string): Promise<Message> =>
    req(`/messages/${mid}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
  deleteMessage: (mid: string): Promise<{ ok: boolean }> =>
    req(`/messages/${mid}`, { method: 'DELETE' }),
  pinMessage: (mid: string): Promise<Message> =>
    req(`/messages/${mid}/pin`, { method: 'POST' }),
  channelPins: (cid: string): Promise<Message[]> => req(`/channels/${cid}/pins`),
  channelMembers: (cid: string): Promise<ChannelMembers> => req(`/channels/${cid}/members`),
  getCanvas: (cid: string): Promise<{ body: string }> => req(`/channels/${cid}/canvas`),
  setCanvas: (cid: string, body: string): Promise<{ body: string }> =>
    req(`/channels/${cid}/canvas`, { method: 'PUT', body: JSON.stringify({ body }) }),

  // Collaborative docs (BlockSuite/yjs) — opaque base64 update log; see docs-sync.ts.
  listDocs: (cid: string): Promise<DocMeta[]> => req(`/docs/channel/${cid}`),
  createDoc: (cid: string, title?: string): Promise<DocMeta> =>
    req('/docs', { method: 'POST', body: JSON.stringify({ channel_id: cid, title }) }),
  getDoc: (docId: string): Promise<{ doc: DocMeta; updates: string[] }> => req(`/docs/${docId}`),
  pushDocUpdate: (docId: string, update: string, origin?: string): Promise<{ ok: boolean }> =>
    req(`/docs/${docId}/update`, { method: 'POST', body: JSON.stringify({ update, origin }) }),
  docSnapshot: (docId: string, snapshot: string, origin?: string): Promise<{ ok: boolean }> =>
    req(`/docs/${docId}/snapshot`, { method: 'POST', body: JSON.stringify({ snapshot, origin }) }),
  members: (wid: string): Promise<PublicUser[]> => req(`/workspaces/${wid}/members`),

  // Agents (bot/FMA management)
  agents: (wid: string): Promise<Agent[]> => req(`/workspaces/${wid}/agents`),
  createAgent: (wid: string, name: string): Promise<{ id: string; name: string; token: string }> =>
    req(`/workspaces/${wid}/agents`, { method: 'POST', body: JSON.stringify({ name }) }),
  rotateAgentToken: (agentId: string): Promise<{ id: string; name: string; token: string }> =>
    req(`/agents/${agentId}/rotate`, { method: 'POST' }),
  scopesCatalog: (): Promise<{ bot: ScopeGroup[]; app: ScopeGroup[] }> => req('/scopes/catalog'),
  setAgentScopes: (wid: string, agentId: string, scopes: string[]): Promise<{ id: string; scopes: string[] }> =>
    req(`/workspaces/${wid}/agents/${agentId}/scopes`, { method: 'PUT', body: JSON.stringify({ scopes }) }),
  getAppToken: (wid: string): Promise<{ exists: boolean; scopes: string[]; last_rotated_at: string | null }> =>
    req(`/workspaces/${wid}/app-token`),
  rotateAppToken: (wid: string, scopes: string[]): Promise<{ token: string; scopes: string[] }> =>
    req(`/workspaces/${wid}/app-token/rotate`, { method: 'POST', body: JSON.stringify({ scopes }) }),

  // Workspace invite links (shareable → direct join, no approval)
  getInvite: (wid: string): Promise<{ code: string; url: string }> => req(`/workspaces/${wid}/invite`),
  rotateInvite: (wid: string): Promise<{ code: string; url: string }> =>
    req(`/workspaces/${wid}/invite/rotate`, { method: 'POST' }),
  inviteInfo: (code: string): Promise<{ workspace: { id: string; name: string; slug: string } }> =>
    req(`/invite/${encodeURIComponent(code)}`),
  acceptInvite: (code: string): Promise<{ id: string; name: string; slug: string; role: string }> =>
    req(`/invite/${encodeURIComponent(code)}/accept`, { method: 'POST' }),
  inviteByEmail: (wid: string, emails: string[], channels: string[] = [], role = 'member'): Promise<{ sent: number }> =>
    req(`/workspaces/${wid}/invite/email`, { method: 'POST', body: JSON.stringify({ emails, channels, role }) }),
  invitations: (wid: string): Promise<{ email: string; role: string; channels: number; created_at: string | null; accepted: boolean }[]> =>
    req(`/workspaces/${wid}/invitations`),
  revokeInvitation: (wid: string, email: string): Promise<{ revoked: number }> =>
    req(`/workspaces/${wid}/invitations/revoke`, { method: 'POST', body: JSON.stringify({ email }) }),

  // Polls
  createPoll: (cid: string, question: string, options: string[], opts: { allow_multi?: boolean; anonymous?: boolean } = {}): Promise<{ poll: PollData; message: Message }> =>
    req(`/channels/${cid}/polls`, { method: 'POST', body: JSON.stringify({ question, options, ...opts }) }),
  getPoll: (pid: string): Promise<PollData> => req(`/polls/${pid}`),
  votePoll: (pid: string, optionIds: string[]): Promise<PollData> =>
    req(`/polls/${pid}/vote`, { method: 'POST', body: JSON.stringify({ option_ids: optionIds }) }),
  closePoll: (pid: string): Promise<PollData> => req(`/polls/${pid}/close`, { method: 'POST' }),

  // Agent prompts (structured questions with selectable options)
  getPrompt: (pid: string): Promise<PromptData> => req(`/prompts/${pid}`),
  answerPrompt: (pid: string, optionIds: string[]): Promise<PromptData> =>
    req(`/prompts/${pid}/answer`, { method: 'POST', body: JSON.stringify({ option_ids: optionIds }) }),

  // Kudos
  giveKudos: (wid: string, toUser: string, reason: string, channelId?: string): Promise<{ ok: boolean }> =>
    req(`/workspaces/${wid}/kudos`, { method: 'POST', body: JSON.stringify({ to_user: toUser, reason, channel_id: channelId }) }),
  kudosLeaderboard: (wid: string): Promise<{ user: PublicUser; count: number }[]> => req(`/workspaces/${wid}/kudos/leaderboard`),
  kudosFeed: (wid: string): Promise<KudosEntry[]> => req(`/workspaces/${wid}/kudos`),

  // Channel checklist
  getChecklist: (cid: string): Promise<ChecklistItem[]> => req(`/channels/${cid}/checklist`),
  addChecklistItem: (cid: string, text: string): Promise<ChecklistItem> =>
    req(`/channels/${cid}/checklist`, { method: 'POST', body: JSON.stringify({ text }) }),
  updateChecklistItem: (id: string, patch: { done?: boolean; text?: string }): Promise<ChecklistItem> =>
    req(`/checklist/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteChecklistItem: (id: string): Promise<{ ok: boolean }> => req(`/checklist/${id}`, { method: 'DELETE' }),

  // Announcements + read receipts
  toggleAnnouncement: (mid: string, announce: boolean): Promise<Message> =>
    req(`/messages/${mid}/announce`, { method: 'POST', body: JSON.stringify({ announce }) }),
  messageReceipts: (mid: string): Promise<{ seen: PublicUser[]; not_seen: PublicUser[]; total: number }> =>
    req(`/messages/${mid}/receipts`),

  // Compliance + workspace admin
  getRetention: (cid: string): Promise<{ retention_days: number | null }> => req(`/channels/${cid}/retention`),
  setRetention: (cid: string, days: number | null): Promise<{ retention_days: number | null }> =>
    req(`/channels/${cid}/retention`, { method: 'PATCH', body: JSON.stringify({ days }) }),
  exportChannel: (cid: string): Promise<{ channel: { id: string; name: string }; exported_at: string; count: number; messages: any[] }> =>
    req(`/channels/${cid}/export`),
  getAdminSettings: (wid: string): Promise<{ dlp_mode: string; donut_enabled: boolean }> =>
    req(`/workspaces/${wid}/admin-settings`),
  setDlp: (wid: string, mode: string): Promise<{ dlp_mode: string }> =>
    req(`/workspaces/${wid}/dlp`, { method: 'PATCH', body: JSON.stringify({ mode }) }),
  setDonut: (wid: string, enabled: boolean): Promise<{ donut_enabled: boolean }> =>
    req(`/workspaces/${wid}/donut`, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
  runDonut: (wid: string): Promise<{ pairs: number }> => req(`/workspaces/${wid}/donut/run`, { method: 'POST' }),
  addAgentToChannel: (
    cid: string,
    agentId: string,
    canPost = true,
  ): Promise<{ channel_id: string; agent_id: string; can_post: string }> =>
    req(`/channels/${cid}/agents`, {
      method: 'POST',
      body: JSON.stringify({ agent_id: agentId, can_post: canPost }),
    }),
  removeAgentFromChannel: (cid: string, agentId: string): Promise<{ ok: boolean }> =>
    req(`/channels/${cid}/agents/${agentId}`, { method: 'DELETE' }),
  deleteAgent: (wid: string, agentId: string): Promise<null> =>
    req(`/workspaces/${wid}/agents/${agentId}`, { method: 'DELETE' }),
  addChannelPeople: (cid: string, userIds: string[]): Promise<{ channel_id: string; added: number }> =>
    req(`/channels/${cid}/people`, { method: 'POST', body: JSON.stringify({ user_ids: userIds }) }),
  removeChannelPerson: (cid: string, userId: string): Promise<{ ok: boolean }> =>
    req(`/channels/${cid}/people/${userId}`, { method: 'DELETE' }),
  removeWorkspaceMember: (wid: string, userId: string): Promise<{ ok: boolean }> =>
    req(`/workspaces/${wid}/members/${userId}`, { method: 'DELETE' }),

  // DMs
  dms: (wid: string): Promise<Dm[]> => req(`/workspaces/${wid}/dms`),
  createDm: (wid: string, memberType: 'user' | 'agent', memberId: string): Promise<Dm> =>
    req(`/workspaces/${wid}/dms`, {
      method: 'POST',
      body: JSON.stringify({ member_type: memberType, member_id: memberId }),
    }),

  // Reactions
  addReaction: (mid: string, emoji: string): Promise<{ ok: boolean }> =>
    req(`/messages/${mid}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) }),
  removeReaction: (mid: string, emoji: string): Promise<{ ok: boolean }> =>
    req(`/messages/${mid}/reactions/${encodeURIComponent(emoji)}`, { method: 'DELETE' }),

  // Typing
  sendTyping: (cid: string): Promise<{ ok: boolean }> =>
    req(`/channels/${cid}/typing`, { method: 'POST' }),

  // File upload (multipart — sent without the JSON Content-Type)
  uploadFile: async (file: File): Promise<Attachment> => {
    const fd = new FormData();
    fd.append('file', file);
    const resp = await fetch(`${API_BASE}/uploads`, {
      method: 'POST',
      headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
      body: fd,
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.detail || `${resp.status} ${resp.statusText}`);
    }
    return resp.json();
  },

  sendMessageWith: async (cid: string, body: string, attachments: Attachment[]): Promise<Message> => {
    const m = await req(`/channels/${cid}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, attachments }),
    });
    window.dispatchEvent(new CustomEvent('fx:message-sent', { detail: { channelId: cid, body: body || '📎 attachment' } }));
    return m;
  },

  // Threads (threaded replies on any message)
  getThread: (mid: string): Promise<{ root: Message; replies: Message[] }> =>
    req(`/messages/${mid}/thread`),
  replyInThread: (mid: string, body: string, attachments: Attachment[] = []): Promise<Message> =>
    req(`/messages/${mid}/thread`, { method: 'POST', body: JSON.stringify({ body, attachments }) }),

  // Calls (LiveKit)
  callsConfig: (): Promise<{ enabled: boolean }> => req('/calls/config'),
  callToken: (convId: string): Promise<{ url: string; room: string; token: string }> =>
    req(`/calls/${convId}/token`),
  ringCall: (convId: string, memberIds: string[], video: boolean): Promise<{ ok: boolean; rung: number }> =>
    req(`/calls/${convId}/ring`, { method: 'POST', body: JSON.stringify({ member_ids: memberIds, video }) }),
  cancelCall: (convId: string): Promise<{ ok: boolean }> =>
    req(`/calls/${convId}/cancel`, { method: 'POST' }),
  declineCall: (convId: string, fromId: string): Promise<{ ok: boolean }> =>
    req(`/calls/${convId}/decline`, { method: 'POST', body: JSON.stringify({ from_id: fromId }) }),
  meetingNotes: (
    cid: string,
    body: { attachment: Attachment; participants: string[]; duration_ms?: number },
  ): Promise<{ ok: boolean; status: string }> =>
    req(`/channels/${cid}/meeting-notes`, { method: 'POST', body: JSON.stringify(body) }),
  createFma: (body: { bot_name: string; tier: string; tos_accepted: boolean }): Promise<{ checkout_url: string }> =>
    req('/me/create-fma', { method: 'POST', body: JSON.stringify(body) }),
  calendarStatus: (): Promise<{ connected: { provider: string; connected_at: string | null }[] }> =>
    req('/me/calendar'),
  calendarConnect: (provider: 'google' | 'microsoft'): Promise<{ authorize_url: string }> =>
    req(`/me/calendar/connect/${provider}`, { method: 'POST' }),
  calendarDisconnect: (provider: 'google' | 'microsoft'): Promise<null> =>
    req(`/me/calendar/${provider}`, { method: 'DELETE' }),
  listCalendarEvents: (opts?: { start?: string; end?: string }): Promise<{ events: CalendarEvent[]; connected: string[] }> => {
    const p = new URLSearchParams();
    if (opts?.start) p.set('start', opts.start);
    if (opts?.end) p.set('end', opts.end);
    const qs = p.toString();
    return req(`/me/calendar/events${qs ? `?${qs}` : ''}`);
  },
  createCalendarEvent: (body: CalendarEventInput): Promise<CalendarEvent> =>
    req('/me/calendar/events', { method: 'POST', body: JSON.stringify(body) }),
  updateCalendarEvent: (id: string, body: Partial<CalendarEventInput>): Promise<CalendarEvent> =>
    req(`/me/calendar/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteCalendarEvent: (id: string): Promise<null> =>
    req(`/me/calendar/events/${id}`, { method: 'DELETE' }),
  // Sticky notes — private per-user personal scratchpad (only the owner ever sees them)
  listStickyNotes: (): Promise<StickyNote[]> => req('/me/sticky-notes'),
  createStickyNote: (body: { body?: string; color?: string; sort?: number }): Promise<StickyNote> =>
    req('/me/sticky-notes', { method: 'POST', body: JSON.stringify(body) }),
  updateStickyNote: (id: string, body: Partial<{ body: string; color: string; sort: number }>): Promise<StickyNote> =>
    req(`/me/sticky-notes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteStickyNote: (id: string): Promise<null> =>
    req(`/me/sticky-notes/${id}`, { method: 'DELETE' }),
  // Jira — per-user API-token connection; search + create issues from chat
  getJira: (): Promise<JiraStatus> => req('/me/jira'),
  connectJira: (body: { base_url: string; email: string; api_token: string }): Promise<JiraStatus> =>
    req('/me/jira/connect', { method: 'POST', body: JSON.stringify(body) }),
  disconnectJira: (): Promise<null> => req('/me/jira', { method: 'DELETE' }),
  listJiraProjects: (): Promise<JiraProject[]> => req('/me/jira/projects'),
  searchJiraIssues: (opts?: { q?: string; mine?: boolean }): Promise<JiraIssue[]> => {
    const p = new URLSearchParams();
    if (opts?.q) p.set('q', opts.q);
    if (opts?.mine === false) p.set('mine', 'false');
    const qs = p.toString();
    return req(`/me/jira/issues${qs ? `?${qs}` : ''}`);
  },
  createJiraIssue: (body: { project_key: string; summary: string; description?: string; issue_type?: string }): Promise<{ key: string; url: string | null }> =>
    req('/me/jira/issues', { method: 'POST', body: JSON.stringify(body) }),
  // Custom profile fields (org-defined)
  listFields: (wid: string): Promise<CustomField[]> =>
    req(`/workspaces/${wid}/fields`),
  createField: (wid: string, body: { label: string; kind?: FieldKind; options?: string[] }): Promise<CustomField> =>
    req(`/workspaces/${wid}/fields`, { method: 'POST', body: JSON.stringify(body) }),
  updateField: (wid: string, fieldId: string, body: { label?: string; options?: string[] }): Promise<CustomField> =>
    req(`/workspaces/${wid}/fields/${fieldId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteField: (wid: string, fieldId: string): Promise<null> =>
    req(`/workspaces/${wid}/fields/${fieldId}`, { method: 'DELETE' }),
  setFieldValues: (wid: string, values: Record<string, string | string[]>): Promise<{ ok: boolean }> =>
    req(`/workspaces/${wid}/fields/values`, { method: 'PUT', body: JSON.stringify({ values }) }),
  memberFields: (wid: string, memberId: string): Promise<MemberField[]> =>
    req(`/workspaces/${wid}/members/${memberId}/fields`),
  memberProfile: (wid: string, memberId: string): Promise<MemberProfile> =>
    req(`/workspaces/${wid}/members/${memberId}/profile`),
  // Team rituals (recurring channel prompts)
  listRituals: (cid: string): Promise<Ritual[]> => req(`/channels/${cid}/rituals`),
  createRitual: (cid: string, body: { prompt: string; cadence: string; day: number; hour_utc: number }): Promise<Ritual> =>
    req(`/channels/${cid}/rituals`, { method: 'POST', body: JSON.stringify(body) }),
  updateRitual: (id: string, body: { enabled?: boolean }): Promise<Ritual> =>
    req(`/rituals/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteRitual: (id: string): Promise<null> => req(`/rituals/${id}`, { method: 'DELETE' }),
  // Role/field-based channel auto-membership rules
  listAutoRules: (cid: string): Promise<AutoRule[]> => req(`/channels/${cid}/auto-rules`),
  createAutoRule: (cid: string, body: { rule_type: string; field_id?: string; match_value: string }): Promise<AutoRule> =>
    req(`/channels/${cid}/auto-rules`, { method: 'POST', body: JSON.stringify(body) }),
  deleteAutoRule: (id: string): Promise<null> => req(`/auto-rules/${id}`, { method: 'DELETE' }),
  // Embeddable chat widget (admin)
  listWidgets: (wid: string): Promise<EmbedWidget[]> => req(`/workspaces/${wid}/embed-widgets`),
  createWidget: (wid: string, body: { name?: string; greeting?: string; accent?: string; require_email?: boolean; allowed_origins?: string }): Promise<EmbedWidget> =>
    req(`/workspaces/${wid}/embed-widgets`, { method: 'POST', body: JSON.stringify(body) }),
  updateWidget: (id: string, body: Partial<{ name: string; greeting: string; accent: string; require_email: boolean; enabled: boolean; allowed_origins: string }>): Promise<EmbedWidget> =>
    req(`/embed-widgets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteWidget: (id: string): Promise<null> => req(`/embed-widgets/${id}`, { method: 'DELETE' }),
  scheduleEvent: (body: { start_utc: string; end_utc: string; title?: string; attendee_ids?: string[]; description?: string; provider?: string }): Promise<{ provider: string; id: string; link?: string }> =>
    req('/calendar/event', { method: 'POST', body: JSON.stringify(body) }),

  // Profile / presence / preferences / notifications
  updateProfile: (p: {
    name?: string;
    avatar_url?: string;
    status_emoji?: string;
    status_text?: string;
    clear_status?: boolean;
    timezone?: string;
  }): Promise<User> => req('/me/profile', { method: 'PATCH', body: JSON.stringify(p) }),
  heartbeat: (): Promise<{ ok: boolean }> => req('/me/heartbeat', { method: 'POST' }),
  setAgentAvatar: (aid: string, avatarUrl: string): Promise<{ id: string; avatar_url: string | null }> =>
    req(`/agents/${aid}/avatar`, { method: 'PATCH', body: JSON.stringify({ avatar_url: avatarUrl }) }),
  updatePresence: (presence: 'active' | 'away'): Promise<User> =>
    req('/me/presence', { method: 'PATCH', body: JSON.stringify({ presence }) }),
  // Status Context + Time Zone Intelligence — one endpoint for status mode/expiry/auto-reply
  // and timezone/work-hours. Send any subset; returns the updated me.
  updateMe: (patch: MePatch): Promise<User> =>
    req('/me/profile', { method: 'PATCH', body: JSON.stringify(patch) }),
  // Group "find a time" — overlapping availability windows across the given users.
  overlap: (
    wid: string,
    body: { user_ids: string[]; duration_min: number; within_days?: number },
  ): Promise<OverlapResult> =>
    req(`/workspaces/${wid}/overlap`, { method: 'POST', body: JSON.stringify(body) }),
  updatePrefs: (prefs: Prefs, theme?: string): Promise<User> =>
    req('/me/prefs', { method: 'PATCH', body: JSON.stringify({ prefs, theme }) }),
  updateNotifications: (paused: boolean, until?: string | null): Promise<User> =>
    req('/me/notifications', { method: 'PATCH', body: JSON.stringify({ paused, until }) }),

  // Web Push (VAPID) — OS-level notifications via the service worker.
  vapidPublicKey: (): Promise<{ key: string }> => req('/push/vapid-public-key'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<{ ok: boolean }> =>
    req('/me/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
  pushUnsubscribe: (endpoint: string): Promise<{ ok: boolean }> =>
    req('/me/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),

  // Global search — with real filters (date range, sender, channel, has-links/reactions,
  // tagged-me, file type). Any subset; a filter-only query (no text) is allowed.
  search: (wid: string, q: string, f: SearchFilters = {}): Promise<SearchResults> => {
    const p = new URLSearchParams({ q });
    if (f.after) p.set('after', f.after);
    if (f.before) p.set('before', f.before);
    if (f.sender) p.set('sender', f.sender);
    if (f.channel) p.set('channel', f.channel);
    if (f.has_links) p.set('has_links', '1');
    if (f.has_reactions) p.set('has_reactions', '1');
    if (f.tagged_me) p.set('tagged_me', '1');
    if (f.file_type) p.set('file_type', f.file_type);
    return req(`/workspaces/${wid}/search?${p.toString()}`);
  },

  // Files + Activity + Sent
  files: (wid: string): Promise<FileItem[]> => req(`/workspaces/${wid}/files`),
  activity: (wid: string): Promise<ActivityItem[]> => req(`/workspaces/${wid}/activity`),
  sent: (): Promise<SentMessage[]> => req('/me/sent'),

  // Read state + scheduling
  readAll: (wid: string): Promise<{ ok: boolean; marked: number }> =>
    req(`/workspaces/${wid}/read_all`, { method: 'POST' }),
  scheduleMessage: (
    cid: string,
    body: string,
    attachments: Attachment[],
    sendAt: string,
  ): Promise<{ id: string; send_at: string }> =>
    req(`/channels/${cid}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ body, attachments, send_at: sendAt }),
    }),
  myScheduled: (): Promise<ScheduledItem[]> => req('/me/scheduled'),
  cancelScheduled: (id: string): Promise<{ ok: boolean }> =>
    req(`/scheduled/${id}`, { method: 'DELETE' }),

  // Incident war rooms
  declareIncident: (
    wid: string,
    body: { title: string; severity?: IncidentSeverity; channel_id?: string },
  ): Promise<{ incident: Incident; channel: Channel }> =>
    req(`/workspaces/${wid}/incidents`, { method: 'POST', body: JSON.stringify(body) }),
  listIncidents: (wid: string): Promise<Incident[]> => req(`/workspaces/${wid}/incidents`),
  channelIncident: (cid: string): Promise<Incident | null> => req(`/channels/${cid}/incident`),
  updateIncident: (
    id: string,
    patch: {
      status?: IncidentStatus;
      severity?: IncidentSeverity;
      commander_id?: string | null;
      title?: string;
      summary?: string | null;
      roles?: Record<string, string>;
    },
  ): Promise<Incident> => req(`/incidents/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  addIncidentEvent: (id: string, kind: 'note' | 'action', body: string): Promise<Incident> =>
    req(`/incidents/${id}/events`, { method: 'POST', body: JSON.stringify({ kind, body }) }),

  // Webhooks
  listIncomingWebhooks: (wid: string): Promise<IncomingWebhook[]> =>
    req(`/workspaces/${wid}/webhooks/incoming`),
  createIncomingWebhook: (wid: string, channelId: string, label: string): Promise<IncomingWebhook> =>
    req(`/workspaces/${wid}/webhooks/incoming`, { method: 'POST', body: JSON.stringify({ channel_id: channelId, label }) }),
  deleteIncomingWebhook: (id: string): Promise<null> =>
    req(`/webhooks/incoming/${id}`, { method: 'DELETE' }),
  listOutgoingWebhooks: (wid: string): Promise<OutgoingWebhook[]> =>
    req(`/workspaces/${wid}/webhooks/outgoing`),
  createOutgoingWebhook: (
    wid: string,
    body: { url: string; label?: string; channel_id?: string; trigger_word?: string },
  ): Promise<OutgoingWebhook> =>
    req(`/workspaces/${wid}/webhooks/outgoing`, { method: 'POST', body: JSON.stringify(body) }),
  deleteOutgoingWebhook: (id: string): Promise<null> =>
    req(`/webhooks/outgoing/${id}`, { method: 'DELETE' }),

  // Vault — workspace-level artifact graph
  vaultNodes: (wid: string, kind?: string, q?: string): Promise<VaultNode[]> => {
    const p = new URLSearchParams();
    if (kind) p.set('kind', kind);
    if (q) p.set('q', q);
    const qs = p.toString();
    return req(`/vault/workspaces/${wid}/nodes${qs ? '?' + qs : ''}`);
  },
  vaultNode: (id: string): Promise<VaultNode> => req(`/vault/nodes/${id}`),
  // Tasks — accepted Helm action items + manual to-dos (vault nodes, kind 'task')
  myTasks: (wid: string, includeDone = false): Promise<VaultNode[]> =>
    req(`/vault/tasks?workspace_id=${wid}${includeDone ? '&include_done=true' : ''}`),
  createTask: (wid: string, title: string): Promise<VaultNode> =>
    req('/vault/tasks', { method: 'POST', body: JSON.stringify({ workspace_id: wid, title }) }),
  toggleTask: (id: string): Promise<VaultNode> => req(`/vault/tasks/${id}/toggle`, { method: 'POST' }),
  deleteTask: (id: string): Promise<void> => req(`/vault/tasks/${id}`, { method: 'DELETE' }),
  setWorkspaceFeatures: (wid: string, patch: Record<string, boolean>): Promise<{ features: Record<string, boolean> }> =>
    req(`/workspaces/${wid}/features`, { method: 'PATCH', body: JSON.stringify(patch) }),
  vaultUpdateNode: (id: string, patch: Partial<Pick<VaultNode, 'title' | 'summary' | 'tags'>>): Promise<VaultNode> =>
    req(`/vault/nodes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  vaultCreateLink: (nodeId: string, targetId: string, linkType?: string): Promise<VaultLink> =>
    req(`/vault/nodes/${nodeId}/links`, {
      method: 'POST',
      body: JSON.stringify({ target_id: targetId, link_type: linkType || 'reference' }),
    }),
  vaultDeleteLink: (linkId: string): Promise<{ ok: boolean }> =>
    req(`/vault/links/${linkId}`, { method: 'DELETE' }),
  vaultPinNode: (channelId: string, nodeId: string): Promise<{ ok: boolean }> =>
    req(`/vault/channels/${channelId}/pins`, {
      method: 'POST',
      body: JSON.stringify({ node_id: nodeId }),
    }),
  vaultUnpinNode: (channelId: string, nodeId: string): Promise<{ ok: boolean }> =>
    req(`/vault/channels/${channelId}/pins/${nodeId}`, { method: 'DELETE' }),
  vaultChannelNodes: (channelId: string, kind?: string): Promise<VaultNode[]> => {
    const qs = kind ? `?kind=${kind}` : '';
    return req(`/vault/channels/${channelId}/vault${qs}`);
  },
  vaultNeighborhood: (nodeId: string, depth?: number): Promise<VaultNeighborhood> =>
    req(`/vault/nodes/${nodeId}/neighborhood${depth ? '?depth=' + depth : ''}`),

  // Suggestions (approval cards)
  mySuggestions: (status?: string, limit?: number): Promise<HelmSuggestion[]> => {
    const ps = new URLSearchParams();
    if (status) ps.set('status', status);
    if (limit) ps.set('limit', String(limit));
    const qs = ps.toString();
    return req(`/vault/me/suggestions${qs ? '?' + qs : ''}`);
  },
  acceptSuggestion: (id: string): Promise<HelmSuggestion> =>
    req(`/vault/suggestions/${id}/accept`, { method: 'POST' }),
  dismissSuggestion: (id: string): Promise<HelmSuggestion> =>
    req(`/vault/suggestions/${id}/dismiss`, { method: 'POST' }),
};

export type ScheduledItem = {
  id: string;
  channel_id: string;
  channel_name?: string | null;
  channel_kind?: string;
  body: string;
  send_at?: string;
};
